import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getPollTemplates,
  createPollTemplate,
  updatePollTemplate,
  deletePollTemplate
} from '../utils/api.js'
import { useFetch } from '../hooks/useFetch.js'
import { hapticImpact, hapticNotification, showConfirm } from '../utils/telegram.js'

const EMPTY_TEMPLATE = {
  title: '',
  question: '',
  options: ['', '', '', ''],
  isAnonymous: false,
  allowsMultipleAnswers: false,
  venueId: '',
  tags: []
}

function TagEditor({ tags, onChange }) {
  const [input, setInput] = useState('')

  const addTag = () => {
    const tag = input.trim().toLowerCase()
    if (!tag || tags.includes(tag)) {
      setInput('')
      return
    }
    onChange([...tags, tag])
    setInput('')
  }

  const removeTag = (tag) => {
    onChange(tags.filter(t => t !== tag))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    } else if (e.key === ',' || e.key === ' ') {
      e.preventDefault()
      addTag()
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {tags.map(tag => (
          <span key={tag} className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1, color: 'inherit', fontSize: 12 }}
              aria-label={`Remove tag ${tag}`}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="form-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. quiz, friends, work"
          style={{ flex: 1 }}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={addTag}>
          Add
        </button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--tg-theme-hint-color)', marginTop: 4 }}>
        Press Enter, Space or comma to add. Tags are case-insensitive.
      </div>
    </div>
  )
}

function PollTemplateForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY_TEMPLATE,
    ...initial,
    options: initial?.options?.length ? initial.options : EMPTY_TEMPLATE.options,
    tags: Array.isArray(initial?.tags) ? initial.tags : []
  }))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const updateOption = (index, value) => {
    const opts = [...form.options]
    opts[index] = value
    setForm(f => ({ ...f, options: opts }))
  }

  const addOption = () => {
    if (form.options.length >= 10) return
    setForm(f => ({ ...f, options: [...f.options, ''] }))
  }

  const removeOption = (index) => {
    if (form.options.length <= 2) return
    setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== index) }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const cleanOptions = form.options.filter(o => o.trim())
    if (cleanOptions.length < 2) {
      setError('At least 2 non-empty options are required')
      return
    }
    setSaving(true)
    setError(null)
    hapticImpact('light')
    try {
      await onSave({ ...form, options: cleanOptions })
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label">Template Name *</label>
        <input
          className="form-input"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Quiz options template"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Options (min 2, max 10)</label>
        <div style={{ fontSize: 11, color: 'var(--tg-theme-hint-color)', marginBottom: 6 }}>
          Maximum 10 options enforced by the Telegram API.
        </div>
        {form.options.map((opt, i) => (
          <div key={i} className="option-row">
            <input
              className="form-input"
              value={opt}
              onChange={e => updateOption(i, e.target.value)}
              placeholder={`Option ${i + 1}`}
            />
            {form.options.length > 2 && (
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeOption(i)}>
                ✕
              </button>
            )}
          </div>
        ))}
        {form.options.length < 10 && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={addOption} style={{ marginTop: 8 }}>
            + Add Option
          </button>
        )}
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isAnonymous}
            onChange={e => setForm(f => ({ ...f, isAnonymous: e.target.checked }))}
          />
          Anonymous poll
        </label>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.allowsMultipleAnswers}
            onChange={e => setForm(f => ({ ...f, allowsMultipleAnswers: e.target.checked }))}
          />
          Allow multiple answers
        </label>
      </div>

      <div className="form-group">
        <label className="form-label">Associative Tags</label>
        <TagEditor
          tags={form.tags}
          onChange={tags => setForm(f => ({ ...f, tags }))}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Venue ID (optional)</label>
        <input
          className="form-input"
          value={form.venueId}
          onChange={e => setForm(f => ({ ...f, venueId: e.target.value }))}
          placeholder="Leave empty to apply to all venues"
        />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Template'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

export default function PollTemplatesPage() {
  const navigate = useNavigate()
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [alert, setAlert] = useState(null)

  const { data, loading, error, refetch } = useFetch(() => getPollTemplates())

  const templates = data?.data || []

  const handleSave = async (form) => {
    if (editingTemplate) {
      await updatePollTemplate(editingTemplate.id, form)
      setAlert({ type: 'success', text: 'Template updated!' })
    } else {
      await createPollTemplate(form)
      setAlert({ type: 'success', text: 'Template created!' })
    }
    hapticNotification('success')
    setShowForm(false)
    setEditingTemplate(null)
    refetch()
  }

  const handleDelete = async (tmpl) => {
    const confirmed = await showConfirm(`Delete "${tmpl.title}"?`)
    if (!confirmed) return
    hapticImpact('medium')
    try {
      await deletePollTemplate(tmpl.id)
      setAlert({ type: 'success', text: 'Template deleted' })
      refetch()
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Delete failed' })
    }
  }

  return (
    <div>
      <button
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 16, marginBottom: 8, width: 'auto' }}
        onClick={() => navigate('/settings')}
      >
        ← Back
      </button>

      <h2 className="section-title">Poll Templates</h2>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.text}
          <button style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setAlert(null)}>✕</button>
        </div>
      )}

      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ marginBottom: 16 }}
          onClick={() => { setEditingTemplate(null); setShowForm(true) }}
        >
          + Create Poll Template
        </button>
      )}

      {showForm && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>{editingTemplate ? 'Edit Template' : 'New Poll Template'}</h3>
          <PollTemplateForm
            initial={editingTemplate}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingTemplate(null) }}
          />
        </div>
      )}

      {loading && <div className="status-message"><div className="loading-spinner" /></div>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && templates.length === 0 && !showForm && (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <div className="empty-state-title">No poll templates yet</div>
          <div className="empty-state-text">Create a template to quickly send polls when new events appear.</div>
        </div>
      )}

      {templates.map(tmpl => (
        <div key={tmpl.id} className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="card-title">{tmpl.title}</div>
              <div className="card-subtitle">Poll title is generated automatically from event name and date</div>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {tmpl.options.map((opt, i) => (
                  <span key={i} className="badge">{opt}</span>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--tg-theme-hint-color)' }}>
                {tmpl.is_anonymous ? '🔒 Anonymous' : '👤 Public'}
                {tmpl.allows_multiple_answers ? ' · Multiple answers' : ''}
                {tmpl.venue_id ? ` · Venue: ${tmpl.venue_id}` : ' · All venues'}
              </div>
              {tmpl.tags && tmpl.tags.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {tmpl.tags.map(tag => (
                    <span key={tag} className="badge" style={{ opacity: 0.7 }}>#{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setEditingTemplate(tmpl); setShowForm(true) }}
              >
                Edit
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleDelete(tmpl)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
